/**
 * Users API Service (TASK-049)
 *
 * API client functions for user management.
 */
import apiClient from './client';
import type {
  User,
  UserFilters,
  PaginatedUsersResponse,
  InviteUserPayload,
  UpdateUserPayload,
  InviteUserResponse,
  PasswordResetResponse,
  CreateCampaignOperatorPayload,
} from '../../types/user';

export const usersApi = {
  /**
   * List users with optional filters
   */
  list: async (filters?: UserFilters): Promise<PaginatedUsersResponse> => {
    const response = await apiClient.get<PaginatedUsersResponse>(
      '/api/v1/users/',
      {
        params: filters,
      }
    );
    return response.data;
  },

  /**
   * Get a single user by ID
   */
  getById: async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/api/v1/users/${id}/`);
    return response.data;
  },

  /**
   * Invite a new user
   */
  invite: async (payload: InviteUserPayload): Promise<InviteUserResponse> => {
    const response = await apiClient.post<InviteUserResponse>(
      '/api/v1/users/invite/',
      payload
    );
    return response.data;
  },

  /**
   * KAN-752: создать оператора-кампанию (без входа в систему и приглашения).
   */
  createCampaignOperator: async (payload: CreateCampaignOperatorPayload): Promise<User> => {
    const response = await apiClient.post<User>('/api/v1/users/campaign-operators/', payload);
    return response.data;
  },

  /**
   * Update a user
   */
  update: async (id: string, payload: UpdateUserPayload): Promise<User> => {
    const response = await apiClient.patch<User>(
      `/api/v1/users/${id}/`,
      payload
    );
    return response.data;
  },

  /**
   * Deactivate a user
   */
  deactivate: async (id: string): Promise<User> => {
    const response = await apiClient.post<User>(
      `/api/v1/users/${id}/deactivate/`
    );
    return response.data;
  },

  /**
   * Reactivate a user
   */
  reactivate: async (id: string): Promise<User> => {
    const response = await apiClient.post<User>(
      `/api/v1/users/${id}/reactivate/`
    );
    return response.data;
  },

  /**
   * Send password reset email
   */
  sendPasswordReset: async (id: string): Promise<PasswordResetResponse> => {
    const response = await apiClient.post<PasswordResetResponse>(
      `/api/v1/users/${id}/reset-password/`
    );
    return response.data;
  },

  /**
   * Resend invitation email
   */
  resendInvitation: async (id: string): Promise<InviteUserResponse> => {
    const response = await apiClient.post<InviteUserResponse>(
      `/api/v1/users/${id}/resend-invitation/`
    );
    return response.data;
  },

  /**
   * Delete a user permanently
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/users/${id}/`);
  },

  /**
   * Fetch the runtime auto-prompt for a given channel (TASK-223).
   */
  getChannelAutoPrompt: async (
    userId: string,
    codename: string
  ): Promise<{ prompt: string }> => {
    const response = await apiClient.get<{ prompt: string }>(
      `/api/v1/users/${userId}/channels/${codename}/auto_prompt/`
    );
    return response.data;
  },

  /**
   * Persist a channel's enabled flag + values (TASK-223).
   */
  updateChannel: async (
    userId: string,
    codename: string,
    body: { enabled: boolean; values: Record<string, unknown> }
  ) => {
    const response = await apiClient.put(
      `/api/v1/users/${userId}/channels/${codename}/`,
      body
    );
    return response.data;
  },
};

export default usersApi;
